import { useState } from 'react';

import type { Settings } from '../persistence/save.js';

/**
 * Master volume, on the menu itself (playtest request).
 *
 * It already lives in Settings, but the one moment a player actually wants it — the
 * music has just started and it is louder than whatever they were listening to — is
 * exactly the moment they do not want to walk into a submenu to find it.
 *
 * Writes straight to the same persisted `Settings` the settings screen edits, so the two
 * are one control in two places rather than two controls that can disagree.
 */
export function MenuVolume({
  settings,
  onChange,
}: {
  settings: Settings;
  onChange: (patch: Partial<Settings>) => void;
}) {
  // What to come back to when unmuting. Deliberately NOT persisted: a save that stored
  // "muted plus a remembered level" would need the pair to stay consistent forever, and
  // the fallback below is a fine answer to the once-per-reload case it costs.
  const [restoreTo, setRestoreTo] = useState(settings.masterVolume || 0.6);
  const muted = settings.masterVolume <= 0;
  const pct = Math.round(settings.masterVolume * 100);

  return (
    <div className="menu-volume">
      <button
        type="button"
        className="menu-volume__mute"
        aria-pressed={muted}
        aria-label={muted ? 'Unmute' : 'Mute'}
        title={muted ? 'Unmute' : 'Mute'}
        onClick={() => {
          if (muted) {
            onChange({ masterVolume: restoreTo > 0 ? restoreTo : 0.6 });
          } else {
            setRestoreTo(settings.masterVolume);
            onChange({ masterVolume: 0 });
          }
        }}
      >
        {muted ? '🔇' : '🔊'}
      </button>

      <input
        className="menu-volume__slider"
        type="range"
        min={0}
        max={1}
        step={0.05}
        value={settings.masterVolume}
        aria-label="Master volume"
        onChange={(e) => {
          const next = Number(e.target.value);
          // Dragging OFF zero is an unmute, so the restore point has to follow the
          // slider — otherwise muting after a drag would snap back to a stale level.
          if (next > 0) setRestoreTo(next);
          onChange({ masterVolume: next });
        }}
      />

      {/* Fixed width, so the slider does not shuffle sideways as the digits change. */}
      <span className="menu-volume__value">{muted ? 'off' : `${pct}%`}</span>
    </div>
  );
}
